import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSessionTimeout } from '@/hooks/useSessionTimeout';
import { SessionTimeoutWarning } from './SessionTimeoutWarning';
import { SessionLockScreen } from './SessionLockScreen';
import { MFAVerification } from './MFAVerification';
import { useToast } from '@/hooks/use-toast';
import {
  clearSessionLockState,
  getSessionLockState,
  setSessionLockState,
} from '@/lib/auth';
import { isSsoSession } from '@/shared/ssoSession';
import { buildAuthRedirectPath } from '@/lib/authReturnTo';

interface SessionManagerProps {
  children: React.ReactNode;
  timeoutMinutes?: number;
  warningMinutes?: number;
}

export function SessionManager({
  children,
  timeoutMinutes = 15,
  warningMinutes = 2,
}: SessionManagerProps) {
  const { toast } = useToast();
  const [isLocked, setIsLocked] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSsoLockedSession, setIsSsoLockedSession] = useState(false);

  // ENT-181: single onAuthStateChange handler with explicit event-type
  // guards. The previous implementation ran getSession() in parallel
  // with onAuthStateChange and both wrote to lock state — a transient
  // INITIAL_SESSION null event (Supabase emits this while restoring
  // session from storage) wiped sessionLockState before the real
  // session arrived, letting a post-timeout refresh bypass MFA.
  //
  // Supabase's onAuthStateChange fires INITIAL_SESSION on subscribe
  // with whatever's in storage, so getSession() is redundant. The
  // single source of truth here makes the race impossible.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        const sessionLockState = getSessionLockState();
        const nextUserId = session.user.id;
        const nextUserEmail = session.user.email || null;

        setUserId(nextUserId);
        setUserEmail(nextUserEmail);
        setIsAuthenticated(true);
        setIsSsoLockedSession(isSsoSession(session));

        if (sessionLockState?.userId === nextUserId) {
          setIsLocked(true);
          return;
        }

        if (sessionLockState) {
          // Lock belongs to a different user — stale, safe to clear.
          clearSessionLockState();
        }
        setIsLocked(false);
        return;
      }

      // Null session. Distinguish a real sign-out (deliberate, must
      // clear lock state) from a transient hydration event
      // (INITIAL_SESSION / TOKEN_REFRESHED / USER_UPDATED with null
      // payload while Supabase is mid-storage-read). Only SIGNED_OUT
      // is a deliberate sign-out signal.
      if (event === 'SIGNED_OUT') {
        clearSessionLockState();
        setIsAuthenticated(false);
        setUserId(null);
        setUserEmail(null);
        setIsLocked(false);
        setMfaFactorId(null);
        setIsSsoLockedSession(false);
      }
      // For any other event with null session: preserve lock state and
      // do not touch isAuthenticated. The next event with a real
      // session will reconcile — if a refresh lands during an active
      // lock, the lock survives.
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleTimeout = useCallback(() => {
    if (userEmail && userId) {
      setSessionLockState({ userId, userEmail });
      setIsLocked(true);
      toast({
        title: 'Session Locked',
        description: 'Your session has been locked due to inactivity.',
      });
    }
  }, [userEmail, userId, toast]);

  const handleWarning = useCallback(() => {
    // Warning is shown via the SessionTimeoutWarning component
  }, []);

  const { isWarningShown, remainingSeconds, extendSession } = useSessionTimeout({
    timeoutMinutes,
    warningMinutes,
    onTimeout: handleTimeout,
    onWarning: handleWarning,
    enabled: isAuthenticated && !isLocked,
  });

  // The protected route stays mounted during reauthentication, but modal
  // libraries commonly portal their content and focus trap to document.body.
  // Close any open dialog before the lock screen receives focus so a portal
  // cannot remain interactive or trap focus outside the inert subtree.
  useEffect(() => {
    if (!isLocked || typeof document === 'undefined') {
      return;
    }

    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
  }, [isLocked]);

  const handleFullLogout = async () => {
    // Use centralized auth utility for consistent global session revocation
    const { signOutGlobally } = await import('@/lib/auth');
    clearSessionLockState();
    setIsLocked(false);
    setMfaFactorId(null);
    await signOutGlobally('/auth', true);
  };

  // SSO sessions cannot unlock in place; "Continue to Sign In" is a forced exit,
  // so carry the current location so the user lands back here after the IdP.
  const handleContinueToSignIn = async () => {
    const { signOutGlobally } = await import('@/lib/auth');
    clearSessionLockState();
    setIsLocked(false);
    setMfaFactorId(null);
    await signOutGlobally(buildAuthRedirectPath(window.location), true);
  };

  const handleUnlock = () => {
    clearSessionLockState();
    setIsLocked(false);
    setMfaFactorId(null);
    extendSession();
    toast({
      title: 'Session Unlocked',
      description: 'Welcome back! Your session has been restored.',
    });
  };

  const handleMFARequired = (factorId: string) => {
    setMfaFactorId(factorId);
  };

  const handleMFASuccess = () => {
    setMfaFactorId(null);
    handleUnlock();
  };

  const handleMFACancel = () => {
    setMfaFactorId(null);
    // Stay on lock screen
  };

  const handleExtendSession = () => {
    extendSession();
  };

  return (
    <>
      {/*
       * Keep the protected subtree mounted during the idle lock. Returning the
       * lock screen in place of `children` unmounted the current route, which
       * discarded its scroll position and all component-local working state
       * before the user had completed password + MFA reauthentication.
       *
       * The subtree is made inert and hidden from assistive technology while
       * either reauthentication screen is present. The lock screens are opaque
       * fixed layers, so keeping it mounted does not expose or permit access to
       * protected content.
       */}
      <div
        aria-hidden={isLocked || undefined}
        className={isLocked ? 'isolate pointer-events-none select-none' : undefined}
        data-testid="session-protected-content"
        inert={isLocked ? '' : undefined}
      >
        {children}
      </div>
      {isLocked && mfaFactorId ? (
        <MFAVerification
          factorId={mfaFactorId}
          onSuccess={handleMFASuccess}
          onCancel={handleMFACancel}
        />
      ) : null}
      {isLocked && userEmail && !mfaFactorId ? (
        <SessionLockScreen
          userId={userId ?? ''}
          userEmail={userEmail}
          isSsoSession={isSsoLockedSession}
          onUnlock={handleUnlock}
          onMFARequired={handleMFARequired}
          onFullLogout={isSsoLockedSession ? handleContinueToSignIn : handleFullLogout}
        />
      ) : null}
      <SessionTimeoutWarning
        isOpen={isWarningShown}
        remainingSeconds={remainingSeconds}
        onExtend={handleExtendSession}
        onLogout={handleFullLogout}
      />
    </>
  );
}
